// Implements: F-UIX-018, F-RPT-009
/**
 * "Exportar" on the consumption screen (UIX.5, RPT.8): a format and one download per report the
 * scope offers. Pure presentation: the format, the report being downloaded and the last refusal
 * are the container's.
 *
 * The costs export mixes two breakdowns of the same spending (by model and by flow version) in
 * one table, so its help says that `cost_micros` adds up only per `breakdown` and `currency`,
 * never across them (7.S13.9); the help is shown under the button and is its accessible description.
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import type { ExportFormat, ExportReport } from "../../../types/reports";
import { EXPORT_FORMATS } from "../model/scopeExports";
import styles from "./MyUsage.module.css";

const LABELS = {
  consumption: "backoffice.exports.consumption",
  mockups: "backoffice.exports.mockups",
  costs: "backoffice.exports.costs",
} as const satisfies Record<ExportReport, string>;

export interface ScopeExportsProps {
  readonly reports: readonly ExportReport[];
  readonly format: ExportFormat;
  /** The report being downloaded, or `null`. */
  readonly downloading: ExportReport | null;
  /** The last download's refusal as the person reads it, or `null`. */
  readonly failure: string | null;
  readonly onFormat: (format: ExportFormat) => void;
  readonly onDownload: (report: ExportReport) => void;
}

export function ScopeExports({
  reports,
  format,
  downloading,
  failure,
  onFormat,
  onDownload,
}: ScopeExportsProps): ReactElement {
  const { t } = useTranslation();
  const ids = useId();
  const headingId = `${ids}-title`;
  const formatId = `${ids}-format`;
  const costsHelpId = `${ids}-costs-help`;

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <h3 id={headingId} className={styles.sectionTitle}>
        {t("backoffice.exports.title")}
      </h3>
      <p className={styles.hint}>{t("backoffice.exports.intro")}</p>
      <p>
        <label htmlFor={formatId}>{t("backoffice.exports.format")}</label>{" "}
        <select
          id={formatId}
          value={format}
          onChange={(event) => {
            onFormat(event.target.value as ExportFormat);
          }}
        >
          {EXPORT_FORMATS.map((option) => (
            <option key={option} value={option}>
              {t(`backoffice.exports.format.${option}`)}
            </option>
          ))}
        </select>
      </p>
      {reports.map((report) => (
        <div key={report}>
          <Button
            variant="secondary"
            type="button"
            loading={downloading === report}
            disabled={downloading !== null}
            describedBy={report === "costs" ? costsHelpId : undefined}
            onClick={() => {
              onDownload(report);
            }}
          >
            {t(LABELS[report])}
          </Button>
          {report === "costs" ? (
            <p id={costsHelpId} className={styles.hint}>
              {t("backoffice.exports.costs.help")}
            </p>
          ) : null}
        </div>
      ))}
      {failure === null ? null : (
        <p className={styles.note} role="alert">
          {failure}
        </p>
      )}
    </section>
  );
}
